(function attachCateringOrderTemplates(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CateringOrderTemplates = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createCateringOrderTemplates() {
  'use strict';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function attr(value) {
    return escapeHtml(value).replaceAll('`', '&#096;');
  }

  function truncate(value, length) {
    const text = String(value || '').trim();
    return text.length > length ? `${text.slice(0, length - 1).trim()}...` : text;
  }

  function image(url, alt, className, fallbackUrl) {
    const source = url || fallbackUrl;
    if (!source) return `<span class="${className} catering-order-redesign__image-placeholder" aria-hidden="true"></span>`;
    return `<img class="${className}" src="${attr(source)}" alt="${attr(alt)}" loading="lazy">`;
  }

  function formatAmount(value) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(value || 0));
  }

  function preferredLabel(value, fallback) {
    return String(value || '').trim() || fallback;
  }

  function modeActions(stage) {
    const isPartyPacks = stage === 'configure';
    const isAlaCarte = stage === 'ala-carte' || stage === 'addons';
    return `
      <nav class="catering-order-redesign__mode-actions" aria-label="Choose your ordering path">
        <button type="button" class="catering-order-redesign__mode-option${isPartyPacks ? ' is-active' : ''} goode-type" aria-current="${isPartyPacks ? 'page' : 'false'}" data-action="show-configure">Party packs</button>
        <button type="button" class="catering-order-redesign__mode-option${isAlaCarte ? ' is-active' : ''} goode-type" aria-current="${isAlaCarte ? 'page' : 'false'}" data-action="show-ala-carte">A la carte</button>
      </nav>`;
  }

  function breadcrumbs(context) {
    const concept = context.concept;
    if (context.stage === 'catalog') return '';
    return `
      <button type="button" data-action="show-catalog">All catering</button>
      <span aria-hidden="true">/</span>
      <span aria-current="page">${escapeHtml(concept ? concept.label : 'Catering')}</span>`;
  }

  function catalog(context) {
    const cards = context.concepts.map((concept) => {
      const pack = concept.pack;
      const presentation = concept.presentation || {};
      const orderable = concept.onlineOrderingEnabled;
      const primaryLabel = preferredLabel(
        presentation.primaryCta,
        orderable ? context.copy.catalogPrimaryCta : context.copy.viewMenusCta,
      );
      const secondaryLabel = preferredLabel(
        presentation.secondaryCta,
        context.copy.bookNowCta,
      );
      return `
        <article class="catering-order-redesign__concept-card${orderable ? ' is-orderable' : ''}">
          <div class="catering-order-redesign__concept-media">
            ${image(presentation.image || pack.image, presentation.imageAlt || presentation.heading || pack.title, 'catering-order-redesign__concept-image', context.placeholderImage)}
          </div>
          <div class="catering-order-redesign__concept-copy">
            <div class="catering-order-redesign__concept-title-row">
              <h2 class="goode-type">${escapeHtml(presentation.heading || pack.title)}</h2>
              ${orderable ? `<strong>${context.money(pack.pricePerPerson)} per person</strong>` : ''}
            </div>
            <p class="goode-type">${escapeHtml(truncate(presentation.description || pack.description || `A ${concept.label} catering package.`, 190))}</p>
            <div class="catering-order-redesign__concept-actions${orderable ? ' catering-order-redesign__concept-actions--single' : ''}">
              ${orderable ? `
                <button class="button button--primary goode-type" type="button" data-action="start-concept" data-concept="${attr(concept.handle)}">${escapeHtml(primaryLabel)}</button>
              ` : `
                <a class="button button--secondary goode-type" href="${attr(presentation.viewMenusLink || context.menuDestination)}">${escapeHtml(primaryLabel)}</a>
                <button class="button button--secondary goode-type" type="button" data-action="open-booking">${escapeHtml(secondaryLabel)}</button>
              `}
            </div>
          </div>
        </article>`;
    }).join('');
    return `
      <section class="catering-order-redesign__catalog" aria-label="Catering concepts">
        <div class="catering-order-redesign__catalog-grid">${cards || '<p>No catering concepts are configured.</p>'}</div>
      </section>`;
  }

  function allocationBar(group) {
    const selected = group.selected || [];
    const assigned = selected.reduce((sum, name) => sum + Number(group.allocations[name] || 0), 0);
    const summary = selected.length ? group.summary : 'Nothing allocated';
    return `
      <span
        class="catering-order-redesign__allocation-bar${selected.length ? '' : ' is-empty'}"
        role="progressbar"
        aria-label="${attr(`${group.label} allocation`)}"
        aria-valuemin="0"
        aria-valuemax="${attr(group.total)}"
        aria-valuenow="${attr(assigned)}"
        aria-valuetext="${attr(summary)}"
      >
        ${selected.map((name, index) => {
          const amount = Number(group.allocations[name] || 0);
          const width = group.total ? Math.max(0, (amount / group.total) * 100) : 0;
          return `<span class="catering-order-redesign__allocation-segment" style="width:${width}%" title="${attr(`${name}: ${formatAmount(amount)} ${group.unit}`)}"></span>`;
        }).join('')}
      </span>`;
  }

  function allocationStepper(group, option) {
    const amount = Number(group.allocations[option] || 0);
    return `
      <div class="catering-order-redesign__allocation-stepper" aria-label="${attr(`${option} allocation`)}">
        <button type="button" data-action="adjust-allocation" data-group="${attr(group.key)}" data-option="${attr(option)}" data-delta="-${group.step}" aria-label="Decrease ${attr(option)}">&minus;</button>
        <label class="catering-order-redesign__allocation-value">
          <span class="visually-hidden">${escapeHtml(option)} ${escapeHtml(group.unit)}</span>
          <input type="number" value="${attr(formatAmount(amount))}" min="${group.minimum}" max="${group.total}" step="${group.step}" inputmode="decimal" data-action="set-allocation" data-group="${attr(group.key)}" data-option="${attr(option)}">
          <span aria-hidden="true">${escapeHtml(group.unit)}</span>
        </label>
        <button type="button" data-action="adjust-allocation" data-group="${attr(group.key)}" data-option="${attr(option)}" data-delta="${group.step}" aria-label="Increase ${attr(option)}">+</button>
      </div>`;
  }

  function allocationChoiceCard(group, option) {
    const selected = group.selected.includes(option);
    const details = group.optionDetails?.[option] || {};
    return `
      <article class="catering-order-redesign__choice-card${selected ? ' is-selected' : ''}">
        <div class="catering-order-redesign__choice-media">
          ${image(details.image, details.imageAlt || option, 'catering-order-redesign__choice-image', group.placeholderImage)}
          ${selected ? allocationStepper(group, option) : ''}
        </div>
        <div class="catering-order-redesign__choice-copy">
          <span class="catering-order-redesign__choice-label goode-type">${escapeHtml(option)}</span>
        </div>
        <button
          class="catering-order-redesign__choice-select"
          type="button"
          aria-pressed="${selected}"
          aria-label="${attr(`${selected ? 'Remove' : 'Select'} ${option}`)}"
          data-action="toggle-choice"
          data-group="${attr(group.key)}"
          data-option="${attr(option)}"
        ></button>
      </article>`;
  }

  function allocationGroup(group) {
    const assigned = group.selected.reduce((sum, name) => sum + Number(group.allocations[name] || 0), 0);
    const orderedOptions = group.options;
    return `
      <section class="catering-order-redesign__choice-group${group.valid ? ' is-valid' : ''}" data-allocation-group="${attr(group.key)}" tabindex="-1">
        ${group.alert ? `<div class="catering-order-redesign__local-alert" role="alert">${escapeHtml(group.alert)}</div>` : ''}
        <header class="catering-order-redesign__choice-group-heading">
          <div>
            <h3 class="goode-type">${escapeHtml(group.label)}<sup aria-hidden="true">*</sup> <span>(Select up to ${group.maximumSelections})</span></h3>
            <p class="goode-type">${group.minimum} ${escapeHtml(group.unit)} minimum per selected ${group.key === 'meat' ? 'meat' : 'side'}.</p>
          </div>
          <strong>${formatAmount(assigned)}/${formatAmount(group.total)} ${escapeHtml(group.unit)}</strong>
        </header>
        ${allocationBar(group)}
        <div class="catering-order-redesign__choice-grid">${orderedOptions.map((option) => allocationChoiceCard(group, option)).join('')}</div>
      </section>`;
  }

  function guestPanel(context) {
    const requirements = context.requirements;
    const disposableWarePacks = typeof context.rules.disposableWarePacks === 'function'
      ? context.rules.disposableWarePacks(context.guests)
      : Math.ceil(Number(context.guests || 0) / 10);
    return `
      <section class="catering-order-redesign__guest-panel" aria-label="Guest count and package calculations">
        <div class="catering-order-redesign__guest-question">
          <div>
            <h3 class="goode-type">How many people are you feeding?</h3>
            <p class="goode-type">Minimum of ${context.rule.minimumGuests}. Change in groups of ${context.rule.guestIncrement}.</p>
          </div>
          <div class="catering-order-redesign__guest-stepper">
            <button type="button" data-action="adjust-guests" data-delta="-${context.rule.guestIncrement}" aria-label="Decrease guests">&minus;</button>
            <label><span class="visually-hidden">Guest count</span><input type="number" value="${context.guests}" min="${context.rule.minimumGuests}" step="${context.rule.guestIncrement}" inputmode="numeric" data-action="set-guests"></label>
            <span>people</span>
            <button type="button" data-action="adjust-guests" data-delta="${context.rule.guestIncrement}" aria-label="Increase guests">+</button>
          </div>
          <label class="catering-order-redesign__disposable-ware">
            <input type="checkbox" data-action="toggle-disposable-ware" ${context.includeDisposableWare ? 'checked' : ''}>
            <span><strong>Include disposable ware</strong><small>${disposableWarePacks} ${disposableWarePacks === 1 ? 'pack' : 'packs'} of 10, included at no charge</small></span>
          </label>
        </div>
        <div class="catering-order-redesign__calculation-summary">
          <dl>
            <div><dt>Meat</dt><dd>${formatAmount(requirements.meat.quantity)} ${escapeHtml(requirements.meat.unit)} (up to ${requirements.meat.maximumSelections} selections)</dd></div>
            <div><dt>Sides</dt><dd>${escapeHtml(context.rules.formatSideQuantity(requirements.sides))}</dd></div>
          </dl>
          <div class="catering-order-redesign__calculation-total"><span>Order total</span><strong>${context.money(context.concept.pack.pricePerPerson * context.guests)}</strong></div>
        </div>
      </section>`;
  }

  function configurator(context) {
    const pack = context.concept.pack;
    return `
      <section class="catering-order-redesign__configure">
        <article class="catering-order-redesign__pack-intro">
          <div class="catering-order-redesign__pack-media">${image(pack.image, pack.title, 'catering-order-redesign__pack-image', context.placeholderImage)}</div>
          <div class="catering-order-redesign__pack-copy">
            <p class="catering-order-redesign__card-eyebrow">${escapeHtml(context.concept.label)}</p>
            <h2 class="goode-type">${escapeHtml(pack.title)}</h2>
            <p class="catering-order-redesign__per-person goode-type">${context.money(pack.pricePerPerson)} per person</p>
            <p class="goode-type">${escapeHtml(pack.description)}</p>
            ${modeActions('configure')}
          </div>
        </article>
        ${guestPanel(context)}
        <div class="catering-order-redesign__choice-groups">${context.groups.map(allocationGroup).join('')}</div>
      </section>`;
  }

  function lineQuantity(line) {
    return `
      <div class="catering-order-redesign__line-quantity" aria-label="${attr(`${line.title} quantity`)}">
        <button type="button" data-action="change-line" data-line-key="${attr(line.key)}" data-quantity="${Math.max(0, line.quantity - 1)}" aria-label="Decrease ${attr(line.title)}">&minus;</button>
        <span>${line.quantity}</span>
        <button type="button" data-action="change-line" data-line-key="${attr(line.key)}" data-quantity="${line.quantity + 1}" aria-label="Increase ${attr(line.title)}">+</button>
      </div>`;
  }

  function alaCarteCard(product, context) {
    const line = product.variantId ? context.linesByVariant.get(String(product.variantId)) : null;
    const sizeOptions = product.sizeOptions || [];
    const optionLabel = product.optionLabel || 'Size';
    const optionPrompt = `Choose a ${String(optionLabel).toLowerCase()}`;
    const quantity = Math.max(1, Number.parseInt(product.quantity, 10) || 1);
    return `
      <article class="catering-order-redesign__addon-card${line ? ' is-selected' : ''}">
        <div class="catering-order-redesign__addon-media">
          ${image(product.image, product.title, 'catering-order-redesign__addon-image', context.placeholderImage || context.concept.pack.image)}
        </div>
        <div class="catering-order-redesign__addon-copy">
          <h3 class="goode-type">${escapeHtml(product.title)}</h3>
          <p class="goode-type">${escapeHtml(truncate(product.description, 110))}</p>
          ${product.requiresSize ? `
            <label class="catering-order-redesign__addon-variant">
              <span>${escapeHtml(optionLabel)}</span>
              <select data-action="set-addon-variant" data-product="${attr(product.key)}" aria-label="${attr(`${product.title} option`)}">
                <option value="">${escapeHtml(optionPrompt)}</option>
                ${sizeOptions.map((variant) => `<option value="${attr(variant.id)}" ${String(variant.id) === String(product.variantId) ? 'selected' : ''}>${escapeHtml(variant.label)} - ${context.money(variant.price)}</option>`).join('')}
              </select>
            </label>` : ''}
          <div class="catering-order-redesign__addon-price"><span>${escapeHtml(product.measure || '')}</span><strong>${product.variantId ? context.money(product.price) : `From ${context.money(product.price)}`}</strong></div>
          <div class="catering-order-redesign__addon-controls">
            <div class="catering-order-redesign__draft-quantity" aria-label="${attr(`${product.title} quantity`)}">
              <button type="button" data-action="adjust-addon-quantity" data-product="${attr(product.key)}" data-delta="-1" aria-label="Decrease ${attr(product.title)} quantity">&minus;</button>
              <label><span class="visually-hidden">${escapeHtml(product.title)} quantity</span><input type="number" min="1" step="1" inputmode="numeric" value="${attr(quantity)}" data-action="set-addon-quantity" data-product="${attr(product.key)}"></label>
              <button type="button" data-action="adjust-addon-quantity" data-product="${attr(product.key)}" data-delta="1" aria-label="Increase ${attr(product.title)} quantity">+</button>
            </div>
            <button class="button button--primary goode-type catering-order-redesign__addon-add" type="button" data-action="add-ala" data-product="${attr(product.key)}" ${product.variantId ? '' : 'disabled'}>Add</button>
          </div>
        </div>
      </article>`;
  }

  function categorySelector(selector, context) {
    const item = selector.items.find((candidate) => candidate.key === selector.itemKey) || null;
    return `
      <article class="catering-order-redesign__category-selector">
        <h3 class="goode-type">${escapeHtml(selector.label)}</h3>
        <label>
          <span>Item</span>
          <select data-action="set-category-item" data-category="${attr(selector.key)}">
            <option value="">${escapeHtml(selector.itemLabel)}</option>
            ${selector.items.map((candidate) => `<option value="${attr(candidate.key)}" ${candidate.key === selector.itemKey ? 'selected' : ''}>${escapeHtml(candidate.title)}</option>`).join('')}
          </select>
        </label>
        <label>
          <span>Size</span>
          <select data-action="set-category-variant" data-category="${attr(selector.key)}" ${item ? '' : 'disabled'}>
            <option value="">Choose a size</option>
            ${(item?.sizeOptions || []).map((variant) => `<option value="${attr(variant.id)}" ${String(variant.id) === String(selector.variantId) ? 'selected' : ''}>${escapeHtml(variant.label)} - ${context.money(variant.price)}</option>`).join('')}
          </select>
        </label>
        <div class="catering-order-redesign__category-selector-footer">
          <div class="catering-order-redesign__draft-quantity" aria-label="${attr(`${selector.label} quantity`)}">
            <button type="button" data-action="adjust-category-quantity" data-category="${attr(selector.key)}" data-delta="-1" aria-label="Decrease ${attr(selector.label)} quantity">&minus;</button>
            <label><span class="visually-hidden">${escapeHtml(selector.label)} quantity</span><input type="number" min="1" step="1" inputmode="numeric" value="${attr(selector.quantity)}" data-action="set-category-quantity" data-category="${attr(selector.key)}"></label>
            <button type="button" data-action="adjust-category-quantity" data-category="${attr(selector.key)}" data-delta="1" aria-label="Increase ${attr(selector.label)} quantity">+</button>
          </div>
          <strong>${selector.variant ? context.money(selector.variant.price) : 'Select item and size'}</strong>
          <button class="button button--primary goode-type" type="button" data-action="add-category" data-category="${attr(selector.key)}" ${selector.variant ? '' : 'disabled'}>Add</button>
        </div>
      </article>`;
  }

  function bandLabel(key) {
    if (key === 'meat') return 'Meats';
    if (key === 'side') return 'Sides';
    if (key === 'drink') return 'Drinks';
    if (key === 'dessert') return 'Desserts';
    return 'More for the Gathering';
  }

  function alaCarte(context) {
    const progress = context.minimumProgress;
    const packPresent = context.packLine != null;
    const groupKeys = context.stage === 'addons'
      ? ['dessert', 'meat', 'side', 'drink', 'other']
      : ['meat', 'side', 'drink', 'dessert', 'other'];
    const groups = groupKeys
      .map((key) => ({ key, products: context.concept.aLaCarteProducts.filter((product) => product.groupKey === key) }))
      .filter((group) => group.products.length);
    return `
      <section class="catering-order-redesign__ala-carte">
        <article class="catering-order-redesign__pack-intro">
          <div class="catering-order-redesign__pack-media">${image(context.concept.pack.image, context.concept.pack.title, 'catering-order-redesign__pack-image', context.placeholderImage)}</div>
          <div class="catering-order-redesign__pack-copy">
            <p class="catering-order-redesign__card-eyebrow">${escapeHtml(context.concept.label)}</p>
            <h2 class="goode-type">${escapeHtml(context.stage === 'addons' ? context.copy.addonsHeading : context.copy.alaCarteHeading)}</h2>
            <p class="goode-type">${escapeHtml(context.stage === 'addons' ? context.copy.addonsBody : context.copy.alaCarteBody)}</p>
            ${modeActions(context.stage)}
          </div>
        </article>
        ${context.minimumCents > 0 && !packPresent ? `
          <div class="catering-order-redesign__minimum${progress.satisfied ? ' is-complete' : ''}">
            <div><strong>${progress.satisfied ? 'Minimum reached' : `${context.money(progress.remaining)} to minimum`}</strong><span>${context.money(context.alaSubtotal)} of ${context.money(context.minimumCents)}</span></div>
            <span class="catering-order-redesign__minimum-bar"><span style="width:${progress.percent}%"></span></span>
          </div>` : ''}
        <div class="catering-order-redesign__addon-groups">
          ${groups.map((group) => `
            <section class="catering-order-redesign__addon-group">
              <h3 class="goode-type">${escapeHtml(bandLabel(group.key))}</h3>
              <div class="catering-order-redesign__addon-grid">${group.products.map((product) => alaCarteCard(product, context)).join('')}</div>
          </section>`).join('') || '<p>No available A la carte products are assigned to this concept yet.</p>'}
        </div>
      </section>`;
  }

  function propertyList(item) {
    const properties = item.properties || {};
    const hidden = new Set(['Ordering mode', 'Concept/menu']);
    return Object.entries(properties)
      .filter(([name, value]) => name[0] !== '_' && value && !hidden.has(name))
      .map(([name, value]) => `<li><span>${escapeHtml(name)}</span><strong>${escapeHtml(value)}</strong></li>`)
      .join('');
  }

  function orderLine(item, context) {
    const pack = context.stateApi.isPackLine(item);
    return `
      <article class="catering-order-redesign__order-line${pack ? ' catering-order-redesign__order-line--pack' : ''}">
        <div class="catering-order-redesign__order-line-heading">
          <div><h3>${escapeHtml(item.product_title || item.title)}</h3><p>${escapeHtml(pack ? `${item.properties?.['Guest count'] || item.quantity} guests` : `${item.quantity} ${item.quantity === 1 ? 'item' : 'items'}`)}</p></div>
          <strong>${context.money(item.final_line_price ?? item.line_price ?? 0)}</strong>
        </div>
        ${pack ? `<ul>${propertyList(item)}</ul>` : lineQuantity(item)}
        <div class="catering-order-redesign__order-line-actions catering-order-redesign__order-line-actions--inline">
          <button type="button" data-action="change-line" data-line-key="${attr(item.key)}" data-quantity="0">Remove</button>
        </div>
      </article>`;
  }

  function stageActions(context, options = {}) {
    if (context.stage === 'catalog') return '';
    const configuring = context.stage === 'configure';
    const packPresent = Boolean(context.packLine);
    const backAction = configuring ? 'show-catalog' : (packPresent ? 'show-configure' : 'show-catalog');
    const backLabel = configuring ? 'Back to catering' : (packPresent ? 'Edit party pack' : 'Back to catering');
    const next = configuring
      ? `<button class="button button--primary goode-type" type="button" data-action="save-pack">${escapeHtml(packPresent ? context.copy.updateOrderCta : context.copy.configureCta)}</button>`
      : `<a class="button button--primary goode-type${context.checkoutAllowed ? '' : ' is-disabled'}" href="${attr(context.checkoutUrl)}" data-action="checkout" aria-disabled="${!context.checkoutAllowed}" ${context.checkoutAllowed ? '' : 'tabindex="-1"'}>${escapeHtml(context.copy.deliveryCta)}</a>`;
    const mobileClass = options.mobile ? ' catering-order-redesign__stage-actions--mobile' : '';
    return `
      <nav class="catering-order-redesign__stage-actions catering-order-redesign__stage-actions--summary${mobileClass}" aria-label="Order progress actions">
        <button class="catering-order-redesign__previous-action goode-type" type="button" data-action="${backAction}">${backLabel}</button>
        ${next}
      </nav>`;
  }

  function orderSummary(context) {
    const draft = context.draftPack;
    const cartItems = Array.isArray(context.cart.items) ? context.cart.items : [];
    const chronologicalAddOns = cartItems
      .map((item, index) => ({ item, index, selectedAt: context.stateApi.lineSelectedAt?.(item) }))
      .filter(({ item }) => !context.stateApi.isPackLine(item))
      .sort((left, right) => {
        if (left.selectedAt != null && right.selectedAt != null) return left.selectedAt - right.selectedAt || left.index - right.index;
        if (left.selectedAt != null) return 1;
        if (right.selectedAt != null) return -1;
        return right.index - left.index;
      })
      .map(({ item }) => item);
    const orderedCartItems = [
      ...cartItems.filter((item) => context.stateApi.isPackLine(item)),
      ...chronologicalAddOns,
    ];
    const empty = orderedCartItems.length === 0;
    return `
      <div class="catering-order-redesign__order-summary">
        <header class="catering-order-redesign__order-summary-header">
          <div><p>${escapeHtml(context.panelEyebrow)}</p><span>Saved automatically</span></div>
          <span class="catering-order-redesign__order-status">${draft ? 'Draft' : `${context.cart.item_count || 0} ${(context.cart.item_count || 0) === 1 ? 'item' : 'items'}`}</span>
        </header>
        <div class="catering-order-redesign__order-summary-body">
          ${draft ? `
            <article class="catering-order-redesign__order-line catering-order-redesign__order-line--draft">
              <div class="catering-order-redesign__order-line-heading catering-order-redesign__order-line-heading--draft">
                <div><h3>${escapeHtml(draft.concept.pack.title)}</h3><p>(${draft.guests} guests)</p></div>
              </div>
              <ul class="catering-order-redesign__summary-rows">
                ${draft.groups.map((group) => `<li><span>${escapeHtml(group.label)}</span><strong>${escapeHtml(group.summary || 'Selection required')}</strong></li>`).join('')}
                <li><span>Included sauce</span><strong>${escapeHtml(context.rules.formatVolume(draft.requirements.sauce.quantity))}</strong></li>
                <li><span>Included bread</span><strong>${escapeHtml(context.rules.formatBread(draft.requirements.bread))}</strong></li>
                <li><span>Disposable ware</span><strong>${draft.includeDisposableWare ? `${context.rules.disposableWarePacks(draft.guests)} packs of 10` : 'Not included'}</strong></li>
              </ul>
            </article>
          ` : empty ? `
            <div class="catering-order-redesign__empty-order"><h2>Start your order.</h2><p>Select a catering format and your choices will stay visible here.</p></div>
          ` : `<div class="catering-order-redesign__order-lines">${orderedCartItems.map((item) => orderLine(item, context)).join('')}</div>`}
        </div>
        <footer class="catering-order-redesign__order-summary-footer">
          <div class="catering-order-redesign__order-totals">
            <div><span>Entree serves</span><strong>${draft ? draft.guests : context.entreeServes}</strong></div>
            <div><span>${draft ? 'Estimated total' : 'Subtotal'}</span><strong>${context.money(draft ? draft.total : (context.cart.total_price || 0))}</strong></div>
          </div>
          ${stageActions(context) || (draft
            ? `<button class="button button--primary goode-type" type="button" data-action="save-pack">${escapeHtml(context.copy.configureCta)}</button>`
            : `<a class="button button--primary goode-type${context.checkoutAllowed ? '' : ' is-disabled'}" href="${attr(context.checkoutUrl)}" data-action="checkout" aria-disabled="${!context.checkoutAllowed}" ${context.checkoutAllowed ? '' : 'tabindex="-1"'}>${escapeHtml(context.copy.deliveryCta)}</a>`)}
          ${!draft && !context.checkoutAllowed && !empty ? `<p class="catering-order-redesign__checkout-note">${escapeHtml(context.checkoutMessage)}</p>` : ''}
        </footer>
      </div>`;
  }

  return Object.freeze({ catalog, configurator, alaCarte, orderSummary, stageActions, breadcrumbs, escapeHtml, bandLabel });
});
